/**
 * @see https://www.compart.com/en/unicode/block
 */
export const UNICODE_BLOCKS = {
	basicLatin: {
		name: "Basic Latin",
		range: "0000-007F",
		exclude: ["0000-001F", "0021-0040", "005B-0060", "007B-007F"],
		allowSpaces: true,
	},
	greekAndCoptic: {
		name: "Greek and Coptic",
		range: "0370-03FF",
		exclude: ["0370-0385", "0387", "03CF-03FF"],
		allowSpaces: true,
	},
	latin1Supplement: {
		name: "Latin-1 Supplement",
		range: "0080-00FF",
		exclude: ["0080-00BF", "00D7", "00F7"],
		allowSpaces: true,
	},
	latinExtendedA: {
		name: "Latin Extended-A",
		range: "0100-017F",
		exclude: [],
		allowSpaces: true,
	},
	latinExtendedB: {
		name: "Latin Extended-B",
		range: "0180-024F",
		exclude: [],
		allowSpaces: true,
	},
} as const;

export function generateRegex(blocks: (keyof typeof UNICODE_BLOCKS)[]) {
	const exclude = blocks
		.map((block) => {
			const ranges = UNICODE_BLOCKS[block].exclude.map((range: string) => {
				if (range.includes("-")) {
					const rangeParts = range.split("-");
					return `(?!.*[\\u${rangeParts[0]}-\\u${rangeParts[1]}])`;
				}

				return `(?!.*\\u${range}${UNICODE_BLOCKS[block].allowSpaces ? "\\s" : ""})`;
			});
			return ranges.join("");
		})
		.join("");

	const regex = `^${exclude}[${blocks
		.map((block) => {
			const rangeParts = UNICODE_BLOCKS[block].range.split("-");
			return `\\u${rangeParts[0]}-\\u${rangeParts[1]}${UNICODE_BLOCKS[block].allowSpaces ? "\\s" : ""}`;
		})
		.join("")}]*$`;

	return regex;
}

export const validationRegex = {
	noSpecialCharacters: {
		names: '^([^$£€¿?¡!|¦()_¬~+`/✅✔✓☑💯☐☒❎✗✘¤¥¦§¨©ª«¬®ˉ°±²³´µ¶¸¹º»™©*{}<>\\\\;:[\\]\\^~="@%&#,.]*)$',
		generic: "^([^$£€¿?¡!|¦()_¬~+`*{}<>\\\\;:[\\]\\^~=]*)$",
		aamsCity: "^([^$£€¿?¡!|¦_¬~+`*{}<>\\\\;:[\\]\\^~=]*)$",
		dgoj: "^([^$&¿?¡!|()@#¬+*{}<>%/\\\\]*)$",
		lotba: "^([^$&¿?¡!|()@#¬+*{}<>%/\\\\]*)$",
		iplyc: "^([^$&¿?¡!|()@#¬+*{}<>%/\\\\]*)$",
		emoji: /^[(\P{Extended_Pictographic})]+$/u,
		segob: "^([^$&¿?¡!|()@#¬+*{}<>%/\\\\]*)$",
		pega: "^([^$£€¿?¡!|¦_¬~+`*{}<>\\\\;:[\\]\\^~=]*)$",
		conajzar: "^([^$£€¿?¡!|¦_¬~+`*{}<>\\\\;:[\\]\\^~=]*)$",
	},
	latinSet: generateRegex(["basicLatin", "latin1Supplement", "latinExtendedA", "latinExtendedB"]),
	optional: {
		noSpecialCharacters: {
			ksa: {
				regex: "^([^$&¿?¡!|()@#¬+*{}<>%]*)([a-zA-Z-'/\\\\])$",
				crsErrorKey: "optionalNoSpecialCharacters",
			},
			coga: {
				regex: "^\\s*[a-zA-ZáéíóúüÁÉÍÓÚÜñÑ']+(?:\\s[a-zA-ZáéíóúüÁÉÍÓÚÜñÑ']+)*\\s*$",
				crsErrorKey: "noSpecialCharacters",
			},
		},
	},
	email: {
		bgc: /[_\p{L}0-9+-]+([.][_\p{L}0-9+-]+)*@([\p{L}0-9]+([\p{L}0-9_-]*[\p{L}0-9]+)?)(\.[\p{L}0-9]+[\p{L}0-9_-]*[\p{L}0-9]+){0,127}(\.\p{L}{2,})/u,
	},
	city: {
		segob: {
			regex: "^[a-zA-ZáéíóúñÑ][a-zA-ZáéíóúñÑ\\s-]*[a-zA-ZáéíóúñÑ]$",
			crsErrorKey: "hyphenOrWhitespaceBetweenWords",
		},
	},
};

export const PASSWORD_VALIDATION_ERRORS = {
	email: "email",
	name: "name",
};

export const PASSWORD_MIN_LENGTH = 8;
